import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {Range} from "slate";
import {useSlate, useSlateSelection} from "slate-react";

import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelSmall} from "@khanacademy/wonder-blocks-typography";

import {LoadingDots} from "../loading-dots.tsx";

import {
    getBlockType,
    isBlockActive,
    setBlockType,
} from "./slate/helpers/block-helpers.ts";
import {
    getInlineCodeNode,
    unwrapInlineCodeNode,
    wrapInlineCodeNode,
} from "./slate/helpers/code-helpers.ts";
import {
    getLinkNode,
    unwrapLinkNode,
    wrapLinkNode,
} from "./slate/helpers/link-helpers.ts";
import {toggleList} from "./slate/helpers/list-helpers.ts";
import {isMarkActive, toggleMark} from "./slate/helpers/mark-helpers.ts";
import {
    getInlineMathNode,
    isMathInlineSelected,
    unwrapInlineMathNode,
    wrapInlineMathNode,
} from "./slate/helpers/math-helpers.ts";
import {
    addColumnAfter,
    addEmptyTable,
    addRowAfter,
    deleteColumn,
    deleteRow,
    deleteTable,
    isSelectionWithinTable,
} from "./slate/helpers/table-helpers.ts";
import StopTypingButton from "./stop-typing-button.tsx";
import ToolButton from "./tool-button.tsx";
import ToolbarBlockSelect from "./toolbar-block-select.tsx";

import redoIcon from "@phosphor-icons/core/regular/arrow-clockwise.svg";
import undoIcon from "@phosphor-icons/core/regular/arrow-counter-clockwise.svg";
import codeIcon from "@phosphor-icons/core/regular/code.svg";
import tableIcon from "@phosphor-icons/core/regular/grid-nine.svg";
import linkIcon from "@phosphor-icons/core/regular/link.svg";
import bulletedListIcon from "@phosphor-icons/core/regular/list-bullets.svg";
import numberedListIcon from "@phosphor-icons/core/regular/list-numbers.svg";
import listPlusIcon from "@phosphor-icons/core/regular/list-plus.svg";
import mathIcon from "@phosphor-icons/core/regular/math-operations.svg";
import readOnlyIcon from "@phosphor-icons/core/regular/pencil-simple-slash.svg";
import boldIcon from "@phosphor-icons/core/regular/text-b.svg";
import italicIcon from "@phosphor-icons/core/regular/text-italic.svg";
import underlineIcon from "@phosphor-icons/core/regular/text-underline.svg";

interface Props {
    toolbarWidth: number | null;
    readOnly?: boolean;
}

const StyledDiv = addStyle("div");

const Separator = () => (
    <StyledDiv role="separator" style={styles.toolbarSeparator} />
);

export default function ToolButtons({
    toolbarWidth,
    readOnly,
}: Props): React.ReactElement {
    const editor = useSlate();
    const selection = useSlateSelection();

    if (readOnly) {
        return (
            <View style={styles.readOnly}>
                <View style={styles.typingText}>
                    <Strut size={spacing.xSmall_8} />
                    <PhosphorIcon icon={readOnlyIcon} size="small" />
                    <Strut size={spacing.xxSmall_6} />
                    <LabelSmall>{t`Khanmigo is typing`}</LabelSmall>
                    <Strut size={spacing.xxxSmall_4} />
                    <LoadingDots
                        color={semanticColor.core.foreground.neutral.strong}
                        size="small"
                    />
                </View>
                <Strut size={spacing.medium_16} />
                <StopTypingButton />
            </View>
        );
    }

    const isRangeSelected = selection && Range.isExpanded(selection);
    const isWithinTable = isSelectionWithinTable(editor);

    const urlText = t`https://example.com`;

    const textFormattingButtons = [
        {
            icon: boldIcon,
            tooltip: t`Bold`,
            active: isMarkActive(editor, "bold"),
            onClick: () => toggleMark(editor, "bold"),
        },
        {
            icon: italicIcon,
            tooltip: t`Italic`,
            active: isMarkActive(editor, "italic"),
            onClick: () => toggleMark(editor, "italic"),
        },
        {
            icon: underlineIcon,
            tooltip: t`Underline`,
            active: isMarkActive(editor, "underline"),
            onClick: () => toggleMark(editor, "underline"),
        },
        {
            icon: codeIcon,
            tooltip: t`Code`,
            active: !!getInlineCodeNode(editor),
            disabled: !isRangeSelected,
            onClick: () => {
                if (getInlineCodeNode(editor)) {
                    unwrapInlineCodeNode(editor);
                } else {
                    wrapInlineCodeNode(editor);
                }
            },
        },
        {
            icon: mathIcon,
            tooltip: t`Math`,
            active: !!getInlineMathNode(editor),
            disabled: !isMathInlineSelected(editor),
            onClick: () => {
                if (getInlineMathNode(editor)) {
                    unwrapInlineMathNode(editor);
                } else {
                    wrapInlineMathNode(editor);
                }
            },
        },
        {
            icon: linkIcon,
            tooltip: t`Link`,
            active: !!getLinkNode(editor),
            disabled: !isRangeSelected,
            onClick: () => {
                if (getLinkNode(editor)) {
                    unwrapLinkNode(editor);
                } else {
                    wrapLinkNode(editor, urlText);
                }
            },
        },
    ];

    const listFormattingButtons = [
        {
            icon: bulletedListIcon,
            tooltip: t`Bulleted list`,
            active: isBlockActive(editor, "bulleted-list"),
            onClick: () => toggleList(editor, "bulleted-list"),
        },
        {
            icon: numberedListIcon,
            tooltip: t`Numbered list`,
            active: isBlockActive(editor, "numbered-list"),
            onClick: () => toggleList(editor, "numbered-list"),
        },
        {
            icon: listPlusIcon,
            tooltip: t`Lettered list`,
            active: isBlockActive(editor, "lettered-list"),
            onClick: () => toggleList(editor, "lettered-list"),
        },
    ];

    const historyCommandButtons = [
        {
            icon: undoIcon,
            tooltip: t`Undo`,
            onClick: () => editor.undo(),
        },
        {
            icon: redoIcon,
            tooltip: t`Redo`,
            onClick: () => editor.redo(),
        },
    ];

    const showTextStyle = toolbarWidth !== null && toolbarWidth > 500;
    const showListButtons = toolbarWidth !== null && toolbarWidth > 600;

    return (
        <>
            {historyCommandButtons.map((button, i) => {
                const {icon, tooltip, onClick} = button;
                return (
                    <ToolButton
                        key={i}
                        icon={icon}
                        tooltip={tooltip}
                        onClick={onClick}
                    />
                );
            })}
            <Separator />
            {showTextStyle && (
                <SingleSelect
                    aria-label={t`Text style`}
                    placeholder={t`Text style`}
                    selectedValue={getBlockType(editor) || "paragraph"}
                    onChange={(newValue) =>
                        setBlockType(editor, newValue as any)
                    }
                    opener={ToolbarBlockSelect}
                    disabled={readOnly}
                >
                    {[
                        {label: t`Paragraph`, value: "paragraph"},
                        {label: t`Title`, value: "heading-one"},
                        {label: t`Heading`, value: "heading-two"},
                        {
                            label: t`Subheading`,
                            value: "heading-three",
                        },
                        {
                            label: t`Block quote`,
                            value: "block-quote",
                        },
                        {
                            label: t`Bulleted list`,
                            value: "bulleted-list",
                        },
                        {
                            label: t`Numbered list`,
                            value: "numbered-list",
                        },
                        {
                            label: t`Lettered list`,
                            value: "lettered-list",
                        },
                        {
                            label: t`Code block`,
                            value: "code-block",
                        },
                    ].map((item, i) => {
                        return (
                            <OptionItem
                                key={i}
                                label={item.label}
                                value={item.value}
                            />
                        );
                    })}
                </SingleSelect>
            )}
            <Separator />
            {textFormattingButtons.map((toolButton, i) => {
                const {icon, tooltip, active, disabled, onClick} = toolButton;
                return (
                    <ToolButton
                        key={i}
                        icon={icon}
                        tooltip={tooltip}
                        active={active}
                        disabled={disabled}
                        onClick={onClick}
                    />
                );
            })}
            {showListButtons && (
                <>
                    <Separator />

                    {listFormattingButtons.map((toolButton, i) => {
                        const {icon, tooltip, active, onClick} = toolButton;
                        return (
                            <ToolButton
                                key={i}
                                icon={icon}
                                tooltip={tooltip}
                                active={active}
                                onClick={onClick}
                            />
                        );
                    })}

                    <Separator />

                    <ToolButton
                        icon={tableIcon}
                        tooltip={t`Table`}
                        onClick={
                            !isWithinTable
                                ? () => addEmptyTable(editor)
                                : undefined
                        }
                        actions={
                            isWithinTable
                                ? [
                                      {
                                          label: t`Insert row below`,
                                          onClick: () => addRowAfter(editor),
                                      },
                                      {
                                          label: t`Insert column after`,
                                          onClick: () => addColumnAfter(editor),
                                      },
                                      {
                                          label: t`Remove row`,
                                          onClick: () => deleteRow(editor),
                                      },
                                      {
                                          label: t`Remove column`,
                                          onClick: () => deleteColumn(editor),
                                      },
                                      {
                                          label: t`Remove table`,
                                          onClick: () => deleteTable(editor),
                                      },
                                  ]
                                : undefined
                        }
                    />
                </>
            )}
        </>
    );
}

const styles = StyleSheet.create({
    toolbarSeparator: {
        borderInlineEnd: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        margin: spacing.xxxSmall_4,
        height: spacing.xLarge_32,
    },
    readOnly: {
        flexDirection: "row",
        alignItems: "center",
    },
    typingText: {
        flexDirection: "row",
        alignItems: "end",
    },
});
